// @givmo/board — the officer writing down a director's assent, and withdrawing one they wrote down.
//
// ══ THE MANUAL PATH IS A FIRST-CLASS PATH, NOT A FALLBACK ═══════════════════════════════════════
//
// Plan §4: "nothing about the module requires 100% director adoption to produce a valid record." A
// director without email, or one who simply will not use a portal, signs on paper and the officer
// records it — and that act COMPLETES a consent exactly as a director's own signature does. The board's
// act does not depend on software adoption, and this surface is where that is true in practice.
//
// ══ WHAT MAKES IT HONEST: THE CLASS, AND THE NOTE ═══════════════════════════════════════════════
//
// The two capture classes are not equivalent, and the wire keeps them apart so the weaker one can never
// be presented as the stronger:
//
//   * the director's own act carries the factor that answered, and names them as the signer;
//   * an officer's entry names NO factor, carries the OFFICER's identity, and the database refuses to
//     let it name the director as the signer at all — so it can never read as the director having acted
//     in their own session.
//
// `provenance_note` is required for the same reason. The module is recording what it was TOLD, not
// something it observed, and the note plus the officer's own identity are what make that visible on the
// act forever. This dialog therefore asks WHERE the assent came from — a signed page received by post,
// a signature returned by email, an assent given by telephone — rather than offering a bare confirm.
//
// ⚠ A WITHDRAWAL IS AN APPENDED ACT, NEVER AN EDIT. The entry it withdraws stays in the log exactly
// where it was; the log is refused UPDATE and DELETE at the database. Withholding consent is the
// director's own remedy under the authority the form library is modeled on, so it is recorded as an act
// rather than erased as a mistake — and it carries its own required note for the same reason the
// signature did.

import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { isGivmoError } from '@givmo/api-client';
import { Button, Dialog, ErrorMessage, Textarea, toast } from '@givmo/design-system';
import { FieldNote } from '../../components/field-note';
import { ReadOnlyDate } from '../../components/read-only-date';
import { useBoardOperations } from '../../board-runtime';

export type RecordedAssentMode = 'record' | 'withdraw';

export interface RecordedAssentDialogProps {
  mode: RecordedAssentMode;
  orgRef: string;
  consentId: string;
  /** The seat whose assent is being written down. */
  boardMemberId: string;
  seatName: string;
  /**
   * The address of the version in force — the exact bytes the director assented to. Echoed on the way
   * in so the act names the text that was in front of them, and refused if the text has since moved.
   */
  contentHash: string;
  /**
   * The date the consent bears (CBG-FIX6). The last assent files the consent as a record dated this
   * day, so recording one shows it. Part of the circulated wording, so it is shown and never edited.
   */
  consentDate?: string | null;
  onClose: () => void;
  onRecorded: () => void;
}

export function RecordedAssentDialog({
  mode,
  orgRef,
  consentId,
  boardMemberId,
  seatName,
  contentHash,
  consentDate = null,
  onClose,
  onRecorded,
}: RecordedAssentDialogProps) {
  const operations = useBoardOperations();
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const isRecording = mode === 'record';

  const submit = useMutation({
    mutationFn: async () => {
      if (isRecording) {
        await operations.officerRecordBoardConsentSignature({
          path: { org_ref: orgRef, consent_id: consentId },
          body: {
            board_member_id: boardMemberId,
            content_hash: contentHash,
            provenance_note: note.trim(),
          },
          throwOnError: true,
        });
      } else {
        await operations.officerRecordBoardConsentRevocation({
          path: { org_ref: orgRef, consent_id: consentId },
          body: { board_member_id: boardMemberId, provenance_note: note.trim() },
          throwOnError: true,
        });
      }
    },
    onSuccess: () => {
      toast.success(isRecording ? 'Recorded on their behalf.' : 'Withdrawal recorded.');
      onRecorded();
    },
    onError: (caught) =>
      setError(
        isGivmoError(caught) ? caught.message : 'We couldn’t record that. Please try again.',
      ),
  });

  return (
    <Dialog.Root open onOpenChange={(next) => !next && !submit.isPending && onClose()}>
      <Dialog.Content
        aria-label={
          isRecording ? 'Record an assent on their behalf' : 'Record a withdrawal on their behalf'
        }
        className="flex flex-col gap-16"
        testId="consent-recorded-assent-dialog"
      >
        <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          {isRecording ? `Record ${seatName}’s assent` : `Record ${seatName}’s withdrawal`}
        </Dialog.Title>

        <p className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
          {isRecording
            ? 'Use this when a board member has given their assent away from Givmo — on paper, by email, or by telephone. It counts towards this consent exactly as their own signature would, and it will always show that you entered it rather than that they signed here.'
            : 'This adds a withdrawal to the record, whoever recorded the signature. What was recorded before stays where it is — the log is only ever added to.'}
        </p>

        {isRecording && consentDate !== null && consentDate.trim().length > 0 ? (
          <ReadOnlyDate
            label="Consent date"
            date={consentDate.trim()}
            testId="consent-recorded-assent-date"
          />
        ) : null}

        <Textarea
          label="Where this came from"
          rows={3}
          value={note}
          onChange={(event) => setNote(event.target.value)}
          testId="consent-recorded-assent-note"
        />
        <FieldNote>
          {isRecording
            ? 'For example: “signed page received by post on 12 May” or “assent given by telephone, witnessed by the chair”. This is kept with the act.'
            : 'For example: “director asked by telephone to withdraw on 14 May”. This is kept with the act.'}
        </FieldNote>

        {error !== null ? (
          <ErrorMessage testId="consent-recorded-assent-error">{error}</ErrorMessage>
        ) : null}

        <div className="flex justify-end gap-10">
          <Button
            appearance="saas"
            tone="secondary"
            disabled={submit.isPending}
            onClick={onClose}
            testId="consent-recorded-assent-cancel"
          >
            Cancel
          </Button>
          <Button
            appearance="saas"
            tone={isRecording ? 'primary' : 'danger'}
            loading={submit.isPending}
            disabled={note.trim().length === 0}
            onClick={() => {
              setError(null);
              submit.mutate();
            }}
            testId="consent-recorded-assent-confirm"
          >
            {isRecording ? 'Record it' : 'Record the withdrawal'}
          </Button>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}
